import { currentAuthEpoch, publishAuthSessionEnded, publishAuthSessionReauthRequired } from "./authSession.js";
import { addClientContextHeaders } from "./clientContext.js";

const CLIENT_VISIT_STARTED_HEADER = "x-truth-client-visit-started";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly data: unknown;

  constructor(message: string, status: number, code = "", data?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export type ApiRequestInit = RequestInit & {
  suppressAuthSessionEnd?: boolean;
};

type ApiResponseErrorOptions = {
  parsedData?: unknown;
  publishAuthSessionEnd?: boolean;
  requestAuthEpoch?: number;
};

function shouldPublishExpiredSession(path: string, status: number) {
  if (status !== 401) return false;
  return path === "/api/auth/me" || !path.startsWith("/api/auth/");
}

export async function apiErrorFromResponse(
  response: Response,
  path: string,
  options: ApiResponseErrorOptions = {}
) {
  const {
    parsedData,
    publishAuthSessionEnd = true,
    requestAuthEpoch
  } = options;
  const data: any = parsedData === undefined ? await response.json().catch(() => ({})) : parsedData;
  const code = typeof data?.error === "string" ? data.error : "";
  const message = typeof data?.message === "string"
    ? data.message
    : code || response.statusText || "request failed";
  if (publishAuthSessionEnd && shouldPublishExpiredSession(path, response.status)) {
    publishAuthSessionEnded("expired", requestAuthEpoch);
  }
  if (response.status === 503 && code === "company_session_reauth_required") {
    publishAuthSessionReauthRequired(requestAuthEpoch);
  }
  return new ApiError(message, response.status, code, data);
}

export async function api<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const requestAuthEpoch = currentAuthEpoch();
  const { suppressAuthSessionEnd = false, ...requestInit } = init;
  const headers = new Headers(requestInit.headers);
  if (requestInit.body && !(requestInit.body instanceof FormData) && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const clientHeaders = addClientContextHeaders(headers);
  const response = await fetch(path, {
    ...requestInit,
    credentials: "include",
    headers: clientHeaders
  });
  reportClientVisitFromResponse(response, path);
  if (!response.ok) {
    throw await apiErrorFromResponse(response, path, {
      publishAuthSessionEnd: !suppressAuthSessionEnd,
      requestAuthEpoch
    });
  }
  const data = await response.json().catch(() => ({}));
  return data as T;
}

export function reportClientVisitFromResponse(response: Response, path: string) {
  if (response.headers.get(CLIENT_VISIT_STARTED_HEADER) === "1"
    && path !== "/api/auth/config" && path !== "/api/client-events/session-start") {
    void recordClientSessionStart();
  }
}

export async function recordClientSessionStart(signal?: AbortSignal) {
  await fetch("/api/client-events/session-start", {
    method: "POST",
    credentials: "include",
    headers: addClientContextHeaders(new Headers({ "content-type": "application/json" })),
    body: "{}",
    signal
  }).catch(() => undefined);
}

export async function streamApi(
  path: string,
  body: unknown,
  onEvent: (event: string, data: any) => void,
  signal?: AbortSignal
) {
  const requestAuthEpoch = currentAuthEpoch();
  const clientHeaders = addClientContextHeaders(new Headers({ "content-type": "application/json" }));
  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: clientHeaders,
    body: JSON.stringify(body),
    signal
  });
  reportClientVisitFromResponse(response, path);
  if (!response.ok || !response.body) {
    if (!response.ok) throw await apiErrorFromResponse(response, path, { requestAuthEpoch });
    throw new ApiError("stream failed", response.status);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split("\n\n");
    buffer = frames.pop() || "";
    for (const frame of frames) {
      const event = frame.match(/^event:\s*(.+)$/m)?.[1];
      const dataText = frame.match(/^data:\s*([\s\S]*)$/m)?.[1];
      if (!event || !dataText) continue;
      onEvent(event, JSON.parse(dataText));
    }
  }
}
